import { graphql } from "@repo/graphql-types/gql";
import type { RecipientQueryQuery, UserQueryQuery } from "@repo/graphql-types/graphql";

import { queryFn } from "@/hooks/use-graphql";
import type { UserContext } from "@/lib/interfaces/user-context";
import type { RootRouteContext } from "@/routes/-root/-root-context";
import { mapToUserContext } from "@/routes/-root/root-map-user-context";

export const ONE_DAY_STALE_TIME = 86400000;

const UserQuery = graphql(/* GraphQL */ `
  query UserQuery {
    usuarios {
      codUsuario
      ativo
      apelido
      codPerfil
      cpf
      email
      nome
      sobrenome
      cartoes(where: { ativo: { _eq: true } }) {
        codUsuarioCartao
      }
      usuarioProfissional: UsuariosProfissionai {
        codConselhoClasse
        bloqueado
        statusDocumentosCaf
        dataInicioDivida
        dataValidacao
        pj
        dominioCriado
        formaDePagamento
        cobrancaAtiva
        usuarioTelefone {
          ramalTroncoNormalizado
        }
      }
      userTermsOfUse: UserTermsOfUse(order_by: { agreed_at: desc }, limit: 1) {
        id
        termsOfUseId: terms_of_use_id
        agreedAt: agreed_at
      }
      especialidades: UsuariosEspecialidades {
        codEspecialidade
        Especialidade {
          nome
        }
      }
    }
    clinicas: tbClinicas {
      codClinica
    }
    usuariosAgendas(where: { ativo: { _eq: true } }, limit: 1) {
      codUsuarioAgenda
    }
    integracoes: tbUsuariosIntegracoes(where: { ativo: { _eq: true } }) {
      codParceiro
      ParceiroIntegracao {
        codModalidadePeriodoGarantido
      }
    }
    usuariosDominiosRegistros(where: { ativo: { _eq: true } }) {
      dominio
    }
    membro {
      altaTaxaCancelamento
      zendeskSessionToken
      funcionalidadesContratadas {
        codFuncionalidade
        ativo
        contratada
      }
      pacotesServicos {
        cancelado
        codPacoteServicos
        dataDesativacao
        dataContratacao
        dataFimTrial
        trialAtivo
        trialFinalizado
        grupoUpgrade
        codUsuarioPacoteServicos
        pacoteServicoPreco {
          valorUnitarioVariavel
          valorUnitarioFixo
        }
      }
      possivelDataFimTrialAssistPro {
        dataFimTrialAssistPro
        temTrialDisponivel
      }
      acoesPromocionais {
        nome
        descricao
        codUsuarioAcaoPromocional
        usuarioAcaoPromocionalDescontos {
          ativo
          taxa
          quantidadeDisponivel
        }
        usuarioAcaoPromocionalFuncionalidade {
          ativo
          nome
        }
      }
      fluxosPendentes: fluxos(where: { status: { eq: "PENDENTE" } }) {
        codFluxo
        codProximoFluxo
        versao
        status
        configuracoes
        obrigatorio
        etapas {
          codEtapa
          dataConcluido
          dataCadastro
          descricao
          status
          configuracoes
          obrigatorio
          resposta
        }
      }
    }
  }
`);

const RecipientQuery = graphql(/* GraphQL */ `
  query RecipientQuery($codUsuario: Int!, $codClinica: Int) {
    expensumRecipient: ExpensumGetRecipient(
      arg1: { codUsuario: $codUsuario, codClinica: $codClinica }
    ) {
      recipient {
        status
      }
    }
  }
`);

export const getUserData = async (authContext: {
  context: RootRouteContext;
  sub: string;
}): Promise<UserContext> => {
  const userQuery = await authContext.context.queryClient.fetchQuery<
    UserQueryQuery | undefined
  >({
    queryKey: ["UserQuery", authContext.sub],
    queryFn: () => queryFn(UserQuery, authContext.context.authClient),
    staleTime: ONE_DAY_STALE_TIME,
  });

  const user = userQuery?.usuarios[0];
  const clinic = userQuery?.clinicas[0];

  let hasRecipientAccount = false;

  try {
    const recipientQuery = user?.codUsuario
      ? await authContext.context.queryClient.fetchQuery<RecipientQueryQuery | undefined>(
          {
            queryKey: ["RecipientQuery", authContext.sub],
            queryFn: () =>
              queryFn(RecipientQuery, authContext.context.authClient, {
                codUsuario: Number(user.codUsuario),
                codClinica: clinic?.codClinica ? Number(clinic.codClinica) : undefined,
              }),
            staleTime: ONE_DAY_STALE_TIME,
          },
        )
      : undefined;

    hasRecipientAccount = Boolean(recipientQuery?.expensumRecipient?.recipient);
  } catch (error) {
    // eslint-disable-next-line no-console -- Apenas para notificar em dev
    console.warn("Erro ao buscar dados da conta de recebedora:", error);
  }

  const authUserSub = await authContext.context.authClient.getUserSub();

  return mapToUserContext({ userQuery, hasRecipientAccount, sub: authUserSub });
};
